import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { GridPager } from "../../common/pagination/GridPager";
import RowsPerPageSelect from "../../common/ui/rows-per-page-select/RowsPerPageSelect";
import PayrollDataGrid, { payrollActionsCellClass, payrollActionsHeaderClass } from "./PayrollDataGrid";
import PayrollModal from "./PayrollModal";
import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { Common } from "../../../utils";
import "../attendance/Attendance.css";
import "../documents/Documents.css";
import "../timesheets/TimeSheets.css";
import "./Payroll.css";

const DEFAULT_PAGE_SIZE = 8;
const { SalaryComponent, TaxPolicy } = config.REST_API;

/** Matches CustomEnums.TaxCalculationType (short). */
const TAX_CALC = { ProgressiveSlab: 0, FixedAmountPerAssignment: 1 };

/**
 * UI row for one progressive tax band (slab). Maps to TaxSlabs: MinAnnualIncome, MaxAnnualIncome, RatePercent.
 * @typedef {{ id: string, apiId: string, lowerBound: string, upperBound: string, taxRatePercent: string, unboundedUpper: boolean }} TaxSlabRow
 */

const emptyTaxSlab = () => ({
  id: `${Date.now()}-${Math.random()}`,
  apiId: "",
  lowerBound: "",
  upperBound: "",
  taxRatePercent: "",
  unboundedUpper: false,
});

/**
 * Maps API TaxSlabs / legacy shapes to UI (PascalCase from .NET).
 * @param {Record<string, unknown>} raw
 * @returns {TaxSlabRow}
 */
const normalizeTaxSlabFromApi = (raw) => {
  const r = raw && typeof raw === "object" ? raw : {};
  const get = (a, b) => (r[a] !== undefined && r[a] !== null ? r[a] : r[b]);
  const minRaw = get("minAnnualIncome", "MinAnnualIncome") ?? get("lowerBound", "LowerBound");
  const maxRaw = get("maxAnnualIncome", "MaxAnnualIncome") ?? get("upperBound", "UpperBound");
  const rate = get("ratePercent", "RatePercent") ?? get("taxRatePercent", "TaxRatePercent") ?? get("rate", "Rate");
  const unboundedFlag = get("unboundedUpper", "UnboundedUpper") ?? get("noUpperLimit", "NoUpperLimit");
  const unboundedUpper = Boolean(unboundedFlag) || maxRaw === null || maxRaw === undefined;
  const idStr = get("id", "Id");
  return {
    id: String(idStr || `${Date.now()}-${Math.random()}`),
    apiId: idStr != null && String(idStr) !== "" ? String(idStr) : "",
    lowerBound: minRaw === undefined || minRaw === null ? "" : String(minRaw),
    upperBound: unboundedUpper ? "" : String(maxRaw),
    taxRatePercent: rate === undefined || rate === null ? "" : String(rate),
    unboundedUpper,
  };
};

const parseDecimalOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
};

const normalizeCalcType = (rawCalc) => {
  if (rawCalc === TAX_CALC.FixedAmountPerAssignment || String(rawCalc) === String(TAX_CALC.FixedAmountPerAssignment)) {
    return TAX_CALC.FixedAmountPerAssignment;
  }
  if (typeof rawCalc === "string") {
    const val = rawCalc.trim().toLowerCase();
    if (val === "fixedamountperassignment" || val === "fixed" || val === "1") return TAX_CALC.FixedAmountPerAssignment;
    if (val === "progressiveslab" || val === "slab" || val === "0") return TAX_CALC.ProgressiveSlab;
  }
  return TAX_CALC.ProgressiveSlab;
};

/** Backend `TaxPolicyListItem.Type` summary: Slabs | Fixed | Mixed | Unknown. */
const mapApiPolicyListType = (raw) => {
  if (typeof raw !== "string") return null;
  const v = raw.trim().toLowerCase();
  if (v === "slabs" || v === "slab" || v === "progressiveslab") return "slab";
  if (v === "fixed" || v === "fixedamount" || v === "fixedamountperassignment") return "fixed";
  if (v === "mixed") return "mixed";
  if (v === "unknown") return "unknown";
  return null;
};

/**
 * TaxSlabs payload for TaxPolicySaveRequest (camelCase JSON).
 * @param {TaxSlabRow[]} slabs
 */
const mapSlabsForSaveRequest = (slabs) =>
  slabs.map((s, index) => {
    /** @type {Record<string, unknown>} */
    const row = {
      minAnnualIncome: parseDecimalOrNull(s.lowerBound) ?? 0,
      maxAnnualIncome: s.unboundedUpper ? null : parseDecimalOrNull(s.upperBound),
      ratePercent: parseDecimalOrNull(s.taxRatePercent) ?? 0,
      sortOrder: index,
      isActive: true,
    };
    if (s.apiId) row.id = s.apiId;
    return row;
  });

/**
 * @param {Record<string, unknown>} raw
 */
const normalizeTaxPolicyListRow = (raw) => {
  const r = raw && typeof raw === "object" ? raw : {};
  const get = (a, b) => (r[a] !== undefined && r[a] !== null ? r[a] : r[b]);
  const id = String(get("id", "Id") ?? "");
  const officeIdRaw = get("officeId", "OfficeId");
  const officeId = officeIdRaw != null && officeIdRaw !== "" ? String(officeIdRaw) : "";
  const officeName = String(get("officeName", "OfficeName") ?? "").trim();
  /** List-item summary from `GetTaxPolicies` (camelCase: type, bands). */
  const listItemType = mapApiPolicyListType(get("type", "Type"));
  const bandsField = get("bands", "Bands");
  const bandsFromApi = bandsField !== undefined && bandsField !== null ? Number(bandsField) : NaN;
  const hasBandsFromApi = Number.isFinite(bandsFromApi);

  const configsRaw = get("configurations", "Configurations");
  const configs = Array.isArray(configsRaw) ? configsRaw : [];
  const sorted = [...configs].sort((a, b) => {
    const sa = Number((a && a.sortOrder) ?? (a && a.SortOrder) ?? 0);
    const sb = Number((b && b.sortOrder) ?? (b && b.SortOrder) ?? 0);
    return sa - sb;
  });
  const activeConfigs = sorted.filter((c) => (c?.isActive ?? c?.IsActive) !== false);
  const cfg = (activeConfigs.length ? activeConfigs : sorted).at(-1);
  const calcRawFromCfg = cfg ? cfg.calculationType ?? cfg.CalculationType : undefined;
  const calcRawFromPolicy = get("calculationType", "CalculationType");
  const calc = normalizeCalcType(calcRawFromCfg ?? calcRawFromPolicy ?? TAX_CALC.ProgressiveSlab);
  const policyTypeRaw = get("policyType", "PolicyType");
  const normalizedPolicyType =
    typeof policyTypeRaw === "string"
      ? policyTypeRaw.trim().toLowerCase()
      : policyTypeRaw === TAX_CALC.FixedAmountPerAssignment
        ? "fixed"
        : policyTypeRaw === TAX_CALC.ProgressiveSlab
          ? "slab"
          : "";

  /** Inferred slab vs fixed when list summary Type is absent. */
  let inferredCategory =
    normalizedPolicyType === "fixed" || normalizedPolicyType === "fixedamountperassignment"
      ? "fixed"
      : normalizedPolicyType === "slab" || normalizedPolicyType === "slabs" || normalizedPolicyType === "progressiveslab"
        ? "slab"
        : calc === TAX_CALC.FixedAmountPerAssignment
          ? "fixed"
          : "slab";

  /** Grid category: slab | fixed | mixed; Unknown uses configuration inference. */
  const typeCategory =
    listItemType === "mixed"
      ? "mixed"
      : listItemType === "slab"
        ? "slab"
        : listItemType === "fixed"
          ? "fixed"
          : inferredCategory;

  const slabSource = (cfg ? cfg.slabs ?? cfg.Slabs : null) ?? get("slabs", "Slabs");
  const slabList = Array.isArray(slabSource) ? slabSource : [];
  const slabCountRaw = get("slabsCount", "SlabsCount") ?? get("taxBands", "TaxBands") ?? get("bandsCount", "BandsCount");
  const legacySlabCount = Number(slabCountRaw);
  const fallbackBandCount = Number.isFinite(legacySlabCount) ? legacySlabCount : slabList.length;

  const showBandCount = typeCategory === "slab" || typeCategory === "mixed";
  const bandsDisplay = showBandCount
    ? hasBandsFromApi
      ? bandsFromApi
      : fallbackBandCount
    : "—";

  return {
    id,
    name: String(get("name", "Name") ?? ""),
    officeId,
    officeName,
    /** @type {'slab' | 'fixed' | 'mixed'} */
    type: typeCategory,
    slabs: bandsDisplay,
    isActive: get("isActive", "IsActive") !== false,
  };
};

/** @param {Record<string, unknown>} row */
const resolveTaxPolicyRowId = (row) => {
  const r = row && typeof row === "object" ? row : {};
  const raw =
    r.id ??
    r.taxPolicyId ??
    r.TaxPolicyId ??
    r.policyId ??
    r.PolicyId ??
    "";
  const s = String(raw ?? "").trim();
  return s !== "" && s !== "undefined" && s !== "null" ? s : "";
};

const normalizeTaxPolicyListResponse = (res) => {
  const body = res?.data;
  const payload = body && typeof body === "object" && body.data && typeof body.data === "object" ? body.data : body;
  const get = (obj, a, b) => (obj && obj[a] !== undefined && obj[a] !== null ? obj[a] : obj ? obj[b] : undefined);
  const itemsRaw = get(payload, "items", "Items");
  const items = Array.isArray(itemsRaw) ? itemsRaw : Array.isArray(payload) ? payload : [];
  const statsRaw = get(payload, "stats", "Stats");
  const stats = statsRaw && typeof statsRaw === "object" ? statsRaw : {};
  const page = Number(get(payload, "page", "Page") ?? 1);
  const take = Number(get(payload, "take", "Take") ?? DEFAULT_PAGE_SIZE);
  const total = Number(get(payload, "total", "Total") ?? items.length);
  return {
    items,
    stats,
    page: Number.isFinite(page) && page > 0 ? page : 1,
    take: Number.isFinite(take) && take > 0 ? take : DEFAULT_PAGE_SIZE,
    total: Number.isFinite(total) && total >= 0 ? total : items.length,
  };
};

const unwrapOne = (res) => {
  const body = res?.data;
  if (body == null || body.success === false) return null;
  if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) return body.data;
  if (typeof body === "object" && !Array.isArray(body)) return body;
  return null;
};

/** @param {import("react-i18next").TFunction} t */
const slabValidationError = (slabs, t) => {
  if (!slabs.length) return t("app.payroll.incomeTax.slabRequired");
  let prevMax = null;
  for (let i = 0; i < slabs.length; i += 1) {
    const s = slabs[i];
    const min = parseDecimalOrNull(s.lowerBound);
    if (min === null) return t("app.payroll.incomeTax.slabLowerRequired");
    if (parseDecimalOrNull(s.taxRatePercent) === null) return t("app.payroll.incomeTax.slabRateRequired");
    if (!s.unboundedUpper && parseDecimalOrNull(s.upperBound) === null) return t("app.payroll.incomeTax.slabUpperRequired");
    if (s.unboundedUpper && i !== slabs.length - 1) return t("app.payroll.incomeTax.slabUnboundedMustBeLast");
    const max = s.unboundedUpper ? null : parseDecimalOrNull(s.upperBound);
    if (max !== null && max < min) return t("app.payroll.incomeTax.slabUpperMustBeGteLower");
    if (prevMax !== null && min <= prevMax) return t("app.payroll.incomeTax.slabRangesMustIncrease");
    prevMax = max;
  }
  return null;
};

/** @typedef {'all' | 'slab' | 'fixed'} TaxTypeTab */

/** @typedef {'fixed_amount' | 'percentage'} FixedTaxAmountKind */

/** @type {[FixedTaxAmountKind, string][]} */
const FIXED_AMOUNT_KIND_OPTIONS = [
  ["fixed_amount", "fixedAmountKindFixed"],
  ["percentage", "fixedAmountKindPercentage"],
];

const unwrapArray = (res) => {
  const body = res?.data;
  if (body == null || body.success === false) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  return [];
};

const normalizeOffice = (raw) => {
  const r = raw && typeof raw === "object" ? raw : {};
  const get = (a, b) => (r[a] !== undefined && r[a] !== null ? r[a] : r[b]);
  return { id: String(get("id", "Id") ?? ""), name: String(get("name", "Name") ?? ""), countryName: String(get("countryName", "CountryName") ?? "") };
};

const IncomeTaxesPage = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(/** @type {"create" | "edit"} */ ("create"));
  const [policyTaxType, setPolicyTaxType] = useState(/** @type {"slab" | "fixed"} */ ("slab"));
  const [modalOfficeId, setModalOfficeId] = useState("");
  const [fixedAmountType, setFixedAmountType] = useState(/** @type {FixedTaxAmountKind} */ ("fixed_amount"));
  const [fixedAmountValue, setFixedAmountValue] = useState("");
  const [slabs, setSlabs] = useState(() => [emptyTaxSlab()]);
  const [filterType, setFilterType] = useState(/** @type {TaxTypeTab} */ ("all"));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [workplaces, setWorkplaces] = useState([]);
  const [loadingWorkplaces, setLoadingWorkplaces] = useState(true);
  const [selectedOfficeId, setSelectedOfficeId] = useState("");
  const [policies, setPolicies] = useState([]);
  const [listStats, setListStats] = useState({ totalPolicies: 0, slabPolicies: 0, fixedPolicies: 0 });
  const [serverTotal, setServerTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [togglingId, setTogglingId] = useState("");
  const [editingPolicyId, setEditingPolicyId] = useState("");
  const [editingConfigurationId, setEditingConfigurationId] = useState("");
  const [policyName, setPolicyName] = useState("");
  const [policyDescription, setPolicyDescription] = useState("");
  const [confirmToggleRow, setConfirmToggleRow] = useState(null);

  /** @param {{ bustCache?: boolean }} [opts] — bustCache avoids stale GET after toggle/save */
  const loadPolicies = useCallback(
    async (opts = {}) => {
      const { bustCache } = opts;
      setLoadingList(true);
      try {
        const policyType = filterType === "all" ? "all" : filterType === "slab" ? "slabs" : "fixed";
        const officeNum = selectedOfficeId ? Number.parseInt(String(selectedOfficeId), 10) : NaN;
        const hasOffice = Number.isFinite(officeNum);

        const buildQuery = (/** @type {number} */ pageNum) => {
          const query = new URLSearchParams({
            page: String(pageNum),
            take: String(pageSize),
            policyType,
            scope: hasOffice ? "office-specific" : "all",
          });
          if (hasOffice) query.set("officeId", String(officeNum));
          if (bustCache) query.set("_", `${Date.now()}-${Math.random()}`);
          return query;
        };

        const fetchPoliciesPage = async (/** @type {number} */ pageNum) => {
          const qs = buildQuery(pageNum).toString();
          return axiosBasic.get(`${TaxPolicy.Get}?${qs}`, {
            ...(bustCache ? { headers: { "Cache-Control": "no-cache", Pragma: "no-cache" } } : {}),
          });
        };

        let res = await fetchPoliciesPage(page);
        let parsed = normalizeTaxPolicyListResponse(res);
        const total = Number(parsed.total);
        const totalSafe = Number.isFinite(total) ? total : 0;
        const totalPages = Math.max(1, Math.ceil(totalSafe / pageSize) || 1);

        if (page > totalPages) {
          setPage(totalPages);
          res = await fetchPoliciesPage(totalPages);
          parsed = normalizeTaxPolicyListResponse(res);
        } else if ((parsed.items?.length ?? 0) === 0 && totalSafe > 0 && page > 1) {
          setPage(1);
          res = await fetchPoliciesPage(1);
          parsed = normalizeTaxPolicyListResponse(res);
        }

        const rows = Array.isArray(parsed.items) ? parsed.items.map((r) => normalizeTaxPolicyListRow(r)) : [];
        const stats = parsed.stats && typeof parsed.stats === "object" ? parsed.stats : {};
        const finalTotal = Number.isFinite(Number(parsed.total)) ? Number(parsed.total) : rows.length;

        setPolicies(rows);
        setServerTotal(finalTotal);
        setListStats({
          totalPolicies: Number(stats.totalPolicies ?? stats.TotalPolicies ?? finalTotal ?? rows.length) || 0,
          slabPolicies: Number(stats.slabPolicies ?? stats.SlabPolicies ?? rows.filter((p) => p.type === "slab").length) || 0,
          fixedPolicies: Number(stats.fixedPolicies ?? stats.FixedPolicies ?? rows.filter((p) => p.type === "fixed").length) || 0,
        });
      } catch {
        Common.showErrorMessage(t("app.payroll.incomeTax.loadListError") || "Could not load tax policies.");
        setPolicies([]);
        setServerTotal(0);
        setListStats({ totalPolicies: 0, slabPolicies: 0, fixedPolicies: 0 });
      } finally {
        setLoadingList(false);
      }
    },
    [TaxPolicy.Get, filterType, page, pageSize, selectedOfficeId, t]
  );

  const loadWorkplaces = useCallback(async () => {
    setLoadingWorkplaces(true);
    try {
      const res = await axiosBasic.get(SalaryComponent.Offices);
      setWorkplaces(unwrapArray(res).map(normalizeOffice).filter((o) => o.id));
    } catch {
      Common.showErrorMessage(t("app.payroll.incomeTax.loadOfficesError") || "Could not load workplaces.");
      setWorkplaces([]);
    } finally {
      setLoadingWorkplaces(false);
    }
  }, [t]);

  useEffect(() => {
    loadWorkplaces();
  }, [loadWorkplaces]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const total = serverTotal;
  const slice = policies;

  const slabCount = listStats.slabPolicies;
  const fixedCount = listStats.fixedPolicies;
  const policyTotalForScope = listStats.totalPolicies;

  useEffect(() => {
    setPage(1);
  }, [filterType, selectedOfficeId]);

  const addSlab = () =>
    setSlabs((rows) => {
      if (!rows.length) return [emptyTaxSlab()];
      const next = [...rows];
      const lastIndex = next.length - 1;
      // Keep payload valid for APIs that only allow a single unbounded top band.
      if (next[lastIndex].unboundedUpper) next[lastIndex] = { ...next[lastIndex], unboundedUpper: false };
      next.push(emptyTaxSlab());
      return next;
    });
  const removeSlab = (id) => setSlabs((rows) => (rows.length <= 1 ? rows : rows.filter((x) => x.id !== id)));

  /** @param {string} id @param {Partial<TaxSlabRow>} patch */
  const updateSlab = useCallback((id, patch) => {
    setSlabs((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }, []);

  const clearFilters = useCallback(() => {
    setFilterType("all");
    setSelectedOfficeId("");
  }, []);

  const applyPolicyDetail = useCallback((detail) => {
    const r = detail && typeof detail === "object" ? detail : {};
    const get = (a, b) => (r[a] !== undefined && r[a] !== null ? r[a] : r[b]);
    setEditingPolicyId(String(get("id", "Id") ?? ""));
    setPolicyName(String(get("name", "Name") ?? ""));
    setPolicyDescription(String(get("description", "Description") ?? ""));
    const oid = get("officeId", "OfficeId");
    setModalOfficeId(oid != null && oid !== "" ? String(oid) : "");
    const configsRaw = get("configurations", "Configurations");
    const configs = Array.isArray(configsRaw) ? configsRaw : [];
    const sorted = [...configs].sort((a, b) => {
      const sa = Number((a && a.sortOrder) ?? (a && a.SortOrder) ?? 0);
      const sb = Number((b && b.sortOrder) ?? (b && b.SortOrder) ?? 0);
      return sa - sb;
    });
    const cfg = sorted[0];
    if (!cfg) {
      setEditingConfigurationId("");
      setPolicyTaxType("slab");
      setSlabs([emptyTaxSlab()]);
      setFixedAmountValue("");
      setFixedAmountType("fixed_amount");
      return;
    }
    const getCfg = (a, b) => (cfg[a] !== undefined && cfg[a] !== null ? cfg[a] : cfg[b]);
    setEditingConfigurationId(String(getCfg("id", "Id") ?? ""));
    const calc = normalizeCalcType(getCfg("calculationType", "CalculationType") ?? TAX_CALC.ProgressiveSlab);
    if (calc === TAX_CALC.FixedAmountPerAssignment) {
      setPolicyTaxType("fixed");
      setFixedAmountType("fixed_amount");
      setFixedAmountValue(String(getCfg("fixedAmount", "FixedAmount") ?? ""));
      setSlabs([emptyTaxSlab()]);
      return;
    }
    setPolicyTaxType("slab");
    setFixedAmountValue("");
    setFixedAmountType("fixed_amount");
    const slabArr = getCfg("slabs", "Slabs");
    if (Array.isArray(slabArr) && slabArr.length) {
      setSlabs(slabArr.map(normalizeTaxSlabFromApi));
    } else {
      setSlabs([emptyTaxSlab()]);
    }
  }, []);

  const openCreate = () => {
    setModalMode("create");
    setEditingPolicyId("");
    setEditingConfigurationId("");
    setPolicyName("");
    setPolicyDescription("");
    setPolicyTaxType("slab");
    setSlabs([emptyTaxSlab()]);
    setFixedAmountType("fixed_amount");
    setFixedAmountValue("");
    setModalOfficeId(selectedOfficeId ? String(selectedOfficeId) : "");
    setModalOpen(true);
  };

  const openEdit = useCallback(
    async (row) => {
      const id = String(row?.id ?? "").trim();
      if (!id) return;
      setLoadingDetail(true);
      try {
        const res = await axiosBasic.get(`${TaxPolicy.DetailBase}${encodeURIComponent(id)}`);
        const detail = unwrapOne(res);
        if (!detail) {
          Common.showErrorMessage(t("app.payroll.incomeTax.loadDetailError") || "Could not load this tax policy.");
          return;
        }
        applyPolicyDetail(detail);
        setModalMode("edit");
        setModalOpen(true);
      } catch {
        Common.showErrorMessage(t("app.payroll.incomeTax.loadDetailError") || "Could not load this tax policy.");
      } finally {
        setLoadingDetail(false);
      }
    },
    [applyPolicyDetail, t]
  );

  const closeModal = () => {
    setModalOpen(false);
    setModalOfficeId("");
    setFixedAmountType("fixed_amount");
    setFixedAmountValue("");
    setEditingPolicyId("");
    setEditingConfigurationId("");
    setPolicyName("");
    setPolicyDescription("");
  };

  const handleSavePolicy = async () => {
    const nameTrim = policyName.trim();
    if (!nameTrim) {
      Common.showErrorMessage(t("app.payroll.incomeTax.nameRequired") || "Enter a name.");
      return;
    }
    if (policyTaxType === "slab") {
      const slabErr = slabValidationError(slabs, t);
      if (slabErr) {
        Common.showErrorMessage(slabErr);
        return;
      }
    }
    setSaving(true);
    try {
      const officeNum = modalOfficeId ? Number.parseInt(String(modalOfficeId), 10) : NaN;
      const officeIdPayload = Number.isFinite(officeNum) ? officeNum : null;

      const calcType = policyTaxType === "fixed" ? TAX_CALC.FixedAmountPerAssignment : TAX_CALC.ProgressiveSlab;
      let fixedAmt = 0;
      if (policyTaxType === "fixed") {
        const v = parseDecimalOrNull(fixedAmountValue);
        fixedAmt = v != null ? v : 0;
      }

      const slabsPayload = policyTaxType === "slab" ? mapSlabsForSaveRequest(slabs) : [];

      /** @type {Record<string, unknown>} */
      const configuration = {
        name: nameTrim,
        calculationType: calcType,
        fixedAmount: policyTaxType === "fixed" ? fixedAmt : 0,
        sortOrder: 0,
        isActive: true,
        slabs: policyTaxType === "slab" ? slabsPayload : [],
      };
      if (editingConfigurationId) configuration.id = editingConfigurationId;
      if (editingPolicyId) configuration.taxPolicyId = editingPolicyId;

      /** @type {Record<string, unknown>} */
      const body = {
        name: nameTrim,
        description: policyDescription.trim(),
        officeId: officeIdPayload,
        isActive: true,
        configurations: [configuration],
      };
      if (modalMode === "edit" && editingPolicyId) body.id = editingPolicyId;

      const res = await axiosBasic.post(TaxPolicy.Save, body);
      const saved = unwrapOne(res);
      if (!saved) {
        Common.showErrorMessage(t("app.payroll.incomeTax.saveError") || "Could not save tax policy.");
        return;
      }
      await loadPolicies({ bustCache: true });
      closeModal();
    } catch {
      Common.showErrorMessage(t("app.payroll.incomeTax.saveError") || "Could not save tax policy.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = useCallback(
    async (e, row) => {
      if (e && typeof e.stopPropagation === "function") e.stopPropagation();
      const id = resolveTaxPolicyRowId(row);
      if (!id) {
        Common.showErrorMessage(t("app.payroll.incomeTax.activateMissingId") || "Missing policy id.");
        return;
      }
      const isActive = row?.isActive !== false;
      setTogglingId(id);
      try {
        await axiosBasic.put(`${TaxPolicy.ToggleActiveBase}${encodeURIComponent(id)}/toggle-active`, {});
        Common.showSuccessMessage(
          isActive
            ? t("app.payroll.incomeTax.softDeleteSuccess") || "Policy deactivated."
            : t("app.payroll.incomeTax.activateSuccess") || "Policy activated."
        );
        await loadPolicies({ bustCache: true });
        setConfirmToggleRow(null);
      } catch {
        Common.showErrorMessage(
          isActive
            ? t("app.payroll.incomeTax.softDeleteError") || "Could not deactivate."
            : t("app.payroll.incomeTax.activateError") || "Could not activate."
        );
      } finally {
        setTogglingId("");
      }
    },
    [TaxPolicy.ToggleActiveBase, loadPolicies, t]
  );

  const openToggleConfirm = useCallback((e, row) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    if (!row) return;
    setConfirmToggleRow(row);
  }, []);

  const officeChipLabel = (o) => (o.countryName ? `${o.name} — ${o.countryName}` : o.name);

  const officeById = useMemo(() => {
    const m = new Map();
    workplaces.forEach((o) => m.set(String(o.id), o));
    return m;
  }, [workplaces]);

  /** Offices shown in modal DDL; include current value if missing from loaded list (e.g. stale API). */
  const modalOfficeOptions = useMemo(() => {
    const list = [...workplaces];
    if (modalOfficeId && !list.some((o) => String(o.id) === String(modalOfficeId))) {
      list.push({
        id: String(modalOfficeId),
        name: t("app.payroll.incomeTax.workplaceUnknownId", { id: modalOfficeId }),
        countryName: "",
      });
    }
    return list;
  }, [workplaces, modalOfficeId, t]);

  const resolveOfficeLabel = useCallback(
    (officeId) => {
      const o = officeById.get(String(officeId ?? ""));
      if (o) return officeChipLabel(o);
      const id = officeId != null && String(officeId) !== "" ? String(officeId) : "";
      return id ? t("app.payroll.incomeTax.workplaceUnknownId", { id }) : "—";
    },
    [officeById, t]
  );

  const workplaceLabelForRow = useCallback(
    (p) => {
      if (p.officeName) return p.officeName;
      if (!p.officeId) return t("app.payroll.common.allOffices");
      return resolveOfficeLabel(p.officeId);
    },
    [resolveOfficeLabel, t]
  );

  const showWorkplaceColumn = selectedOfficeId === "";
  const closeConfirmToggle = useCallback(() => {
    if (togglingId) return;
    setConfirmToggleRow(null);
  }, [togglingId]);

  const columns = useMemo(() => {
    const workplaceCol = {
      key: "workplace",
      label: t("app.payroll.incomeTax.colWorkplace"),
      sortable: false,
      headerClassName: "payroll-it-th-workplace",
    };
    const rest = [
      { key: "name", label: t("app.payroll.incomeTax.colName"), sortable: false, headerClassName: "payroll-it-th-name" },
      ...(showWorkplaceColumn ? [workplaceCol] : []),
      { key: "type", label: t("app.payroll.incomeTax.colType"), sortable: false, headerClassName: "payroll-it-th-type" },
      { key: "slabs", label: t("app.payroll.incomeTax.colSlabs"), sortable: false, headerClassName: "payroll-it-th-slabs" },
      { key: "status", label: t("app.payroll.common.status"), sortable: false, headerClassName: "payroll-it-th-status" },
      { key: "actions", label: t("app.payroll.common.actions"), sortable: false, headerClassName: payrollActionsHeaderClass },
    ];
    return rest;
  }, [showWorkplaceColumn, t]);

  return (
    <div className="documents-page payroll-page ts-page px-3 px-md-4 py-4">
      <CommonPageHeader
        icon={<i className="bi bi-percent" />}
        title={t("app.payroll.incomeTax.titleCased") || t("app.payroll.incomeTax.title")}
        description={t("app.payroll.incomeTax.subtitle")}
      />

      <section className="payroll-it-filter-panel mb-3" aria-label={t("app.payroll.incomeTax.filterToolbarLabel")}>
        <div className="payroll-it-filter-panel__shell">
          <header className="payroll-it-filter-panel__head">
            <div className="payroll-it-filter-panel__intro">
              <h2 className="payroll-it-filter-panel__title">{t("app.payroll.incomeTax.filterToolbarLabel")}</h2>
              <p className="payroll-it-filter-panel__lead">{t("app.payroll.incomeTax.filterHint")}</p>
            </div>
            <div className="payroll-it-filter-panel__actions">
              <button type="button" className="payroll-it-filter-panel__btn payroll-it-filter-panel__btn--ghost" onClick={clearFilters}>
                <span className="payroll-it-filter-panel__btn-icon" aria-hidden>
                  <i className="bi bi-arrow-counterclockwise" />
                </span>
                <span className="payroll-it-filter-panel__btn-text">{t("app.payroll.common.clearFilters")}</span>
              </button>
              <button type="button" className="payroll-it-filter-panel__btn payroll-it-filter-panel__btn--primary" onClick={openCreate}>
                <span className="payroll-it-filter-panel__btn-icon payroll-it-filter-panel__btn-icon--inverse" aria-hidden>
                  <i className="bi bi-plus-lg" />
                </span>
                <span className="payroll-it-filter-panel__btn-text">{t("app.payroll.incomeTax.newPolicy")}</span>
              </button>
            </div>
          </header>
          <div className="payroll-it-filter-panel__body">
            <div className="payroll-it-filter-panel__row">
              <div className="payroll-it-filter-panel__row-head">
                <i className="bi bi-building payroll-it-filter-panel__row-icon" aria-hidden />
                <span className="payroll-it-filter-panel__row-label">{t("app.payroll.incomeTax.workplaceSelectorLabel")}</span>
              </div>
              <div
                className="payroll-it-filter-panel__chips"
                role="tablist"
                aria-label={t("app.payroll.incomeTax.workplaceSelectorAria")}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={selectedOfficeId === ""}
                  className={`payroll-it-filter-chip ${selectedOfficeId === "" ? "payroll-it-filter-chip--active" : ""}`}
                  onClick={() => setSelectedOfficeId("")}
                >
                  {t("app.payroll.common.allOffices")}
                </button>
                {workplaces.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    role="tab"
                    aria-selected={selectedOfficeId === String(o.id)}
                    disabled={loadingWorkplaces}
                    className={`payroll-it-filter-chip ${selectedOfficeId === String(o.id) ? "payroll-it-filter-chip--active" : ""}`}
                    onClick={() => setSelectedOfficeId(String(o.id))}
                  >
                    {officeChipLabel(o)}
                  </button>
                ))}
              </div>
            </div>
            <div className="payroll-it-filter-panel__divider" aria-hidden />
            <div className="payroll-it-filter-panel__row">
              <div className="payroll-it-filter-panel__row-head">
                <i className="bi bi-funnel payroll-it-filter-panel__row-icon" aria-hidden />
                <span className="payroll-it-filter-panel__row-label">{t("app.payroll.incomeTax.filterTypeLabel")}</span>
              </div>
              <div className="payroll-it-segmented" role="tablist" aria-label={t("app.payroll.incomeTax.filterTypeLabel")}>
                {(
                  /** @type {const} */ ([
                    ["all", "filterTypeAll"],
                    ["slab", "tabSlabShort"],
                    ["fixed", "tabFixedShort"],
                  ])
                ).map(([key, i18nKey]) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={filterType === key}
                    className={filterType === key ? "payroll-it-segmented__btn is-active" : "payroll-it-segmented__btn"}
                    onClick={() => setFilterType(/** @type {TaxTypeTab} */ (key))}
                  >
                    {t(`app.payroll.incomeTax.${i18nKey}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="payroll-sc-stats-wrap mb-3" aria-label={t("app.payroll.common.statsAria")}>
        <div className="ts-appr-stats payroll-sc-summary-stats">
          <div className="ts-appr-stat-card ts-appr-stat-card--total">
            <div className="ts-appr-stat-card__accent" aria-hidden />
            <div className="ts-appr-stat-card__inner">
              <div className="ts-appr-stat-card__top">
                <span className="ts-appr-stat-card__icon">
                  <i className="bi bi-journal-text" />
                </span>
                <div className="ts-appr-stat-card__figures">
                  <span className="ts-appr-stat-card__value">{policyTotalForScope}</span>
                  <span className="ts-appr-stat-card__label">{t("app.payroll.incomeTax.statPolicies")}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="ts-appr-stat-card ts-appr-stat-card--awaiting">
            <div className="ts-appr-stat-card__accent" aria-hidden />
            <div className="ts-appr-stat-card__inner">
              <div className="ts-appr-stat-card__top">
                <span className="ts-appr-stat-card__icon">
                  <i className="bi bi-bar-chart-steps" />
                </span>
                <div className="ts-appr-stat-card__figures">
                  <span className="ts-appr-stat-card__value">{String(slabCount).padStart(2, "0")}</span>
                  <span className="ts-appr-stat-card__label">{t("app.payroll.incomeTax.statSlabPolicies")}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="ts-appr-stat-card ts-appr-stat-card--approved">
            <div className="ts-appr-stat-card__accent" aria-hidden />
            <div className="ts-appr-stat-card__inner">
              <div className="ts-appr-stat-card__top">
                <span className="ts-appr-stat-card__icon">
                  <i className="bi bi-currency-dollar" />
                </span>
                <div className="ts-appr-stat-card__figures">
                  <span className="ts-appr-stat-card__value">{String(fixedCount).padStart(2, "0")}</span>
                  <span className="ts-appr-stat-card__label">{t("app.payroll.incomeTax.statFixedPolicies")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div
        className={`payroll-sc-panel payroll-it-policies-grid card border-0 shadow-sm ${showWorkplaceColumn ? "payroll-it-policies-grid--with-office-col" : ""}`}
      >
        <div className="card-body p-3">
          <div className="payroll-sc-data-card__head">
            <h3 className="payroll-sc-data-card__title">{t("app.payroll.incomeTax.tableListTitle")}</h3>
            <RowsPerPageSelect
              value={pageSize}
              onChange={(next) => {
                setPageSize(next);
                setPage(1);
              }}
              options={[8, 20, 50, 100]}
              label="Rows per page"
              ariaLabel="Rows per page"
              wrapperClassName="documents-filter-page-size"
              labelClassName="documents-filter-page-size-label"
              selectClassName="documents-input documents-filter-select documents-filter-page-size-ddl"
            />
          </div>
          <PayrollDataGrid
            columns={columns}
            rows={slice}
            getRowKey={(row) => String(row.id)}
            emptyMessage={t("app.payroll.common.noResults")}
            loading={loadingList}
            loadingMessage={t("app.payroll.incomeTax.loadingList")}
            onRowClick={(row) => openEdit(row)}
            renderRow={(p) => {
              const rowId = resolveTaxPolicyRowId(p);
              const rowToggleBusy = Boolean(togglingId && togglingId === rowId);
              return (
              <>
                <td className="fw-semibold payroll-it-td-name">{p.name}</td>
                {showWorkplaceColumn ? (
                  <td className="payroll-it-td-workplace">
                    <span className="payroll-it-workplace-pill" title={workplaceLabelForRow(p)}>
                      <i className="bi bi-geo-alt-fill payroll-it-workplace-pill__icon" aria-hidden />
                      <span className="payroll-it-workplace-pill__text">{workplaceLabelForRow(p)}</span>
                    </span>
                  </td>
                ) : null}
                <td className="payroll-it-td-type">
                  <span
                    className={`payroll-it-type-badge ${
                      p.type === "slab"
                        ? "payroll-it-type-badge--slab"
                        : p.type === "fixed"
                          ? "payroll-it-type-badge--fixed"
                          : "payroll-it-type-badge--mixed"
                    }`}
                    title={
                      p.type === "fixed"
                        ? t("app.payroll.incomeTax.typeFixed")
                        : p.type === "mixed"
                          ? t("app.payroll.incomeTax.typeMixed")
                          : t("app.payroll.incomeTax.typeSlab")
                    }
                  >
                    {p.type === "fixed"
                      ? t("app.payroll.incomeTax.typeFixed")
                      : p.type === "mixed"
                        ? t("app.payroll.incomeTax.typeMixed")
                        : t("app.payroll.incomeTax.typeSlab")}
                  </span>
                </td>
                <td className="payroll-it-td-slabs">{p.slabs}</td>
                <td className="payroll-it-td-status">
                  {p.isActive ? (
                    <span className="payroll-badge payroll-badge--active">{t("app.payroll.common.statusActive")}</span>
                  ) : (
                    <span className="payroll-badge payroll-badge--draft">{t("app.payroll.common.statusInactive")}</span>
                  )}
                </td>
                <td className={payrollActionsCellClass} onClick={(e) => e.stopPropagation()}>
                  <div className="documents-actions-inline payroll-it-grid-actions">
                    <button
                      type="button"
                      className="documents-grid-action-btn documents-grid-action-btn--edit"
                      onClick={() => openEdit(p)}
                      disabled={Boolean(togglingId)}
                    >
                      {t("app.payroll.common.edit")}
                    </button>
                    {p.isActive ? (
                      <button
                        type="button"
                        className="documents-grid-action-btn documents-grid-action-btn--delete"
                        onClick={(e) => openToggleConfirm(e, p)}
                        disabled={rowToggleBusy}
                      >
                        {rowToggleBusy ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                            {t("app.payroll.common.deactivate")}
                          </>
                        ) : (
                          t("app.payroll.common.deactivate")
                        )}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="documents-grid-action-btn documents-grid-action-btn--view"
                        onClick={(e) => openToggleConfirm(e, p)}
                        disabled={rowToggleBusy}
                      >
                        {rowToggleBusy ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                            {t("app.payroll.common.activate")}
                          </>
                        ) : (
                          t("app.payroll.common.activate")
                        )}
                      </button>
                    )}
                  </div>
                </td>
              </>
              );
            }}
          />

          <div className="documents-pagination documents-pagination--grid-pager d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2 pt-1">
            <span className="documents-pagination-total text-muted small">
              {total === 0
                ? t("app.payroll.common.noResults")
                : t("app.payroll.common.showingRange", {
                    from: (page - 1) * pageSize + 1,
                    to: Math.min((page - 1) * pageSize + slice.length, total),
                    total,
                  })}
            </span>
            <GridPager
              className="documents-rc-pagination"
              onChange={(p) => setPage(p)}
              current={page}
              total={total}
              pageSize={pageSize}
              defaultPageSize={pageSize}
              showLessItems
              showTitle={false}
              showSizeChanger={false}
            />
          </div>
        </div>
      </div>

      <PayrollModal
        open={Boolean(confirmToggleRow)}
        onClose={closeConfirmToggle}
        title={confirmToggleRow?.isActive ? t("app.payroll.common.deactivate") : t("app.payroll.common.activate")}
        subtitle={
          confirmToggleRow?.isActive
            ? t("app.payroll.incomeTax.softDeleteConfirm")
            : t("app.payroll.incomeTax.activateConfirm")
        }
        titleId="payroll-income-tax-toggle-modal-title"
        size="small"
        variant="salary"
        headerIcon={<i className={`bi ${confirmToggleRow?.isActive ? "bi-pause-circle" : "bi-play-circle"}`} />}
        footer={
          <>
            <button type="button" className="att-btn att-btn--ghost" onClick={closeConfirmToggle} disabled={Boolean(togglingId)}>
              {t("app.payroll.common.cancel")}
            </button>
            <button
              type="button"
              className="att-btn att-btn--save-primary"
              onClick={(e) => handleToggleActive(e, confirmToggleRow)}
              disabled={Boolean(togglingId)}
            >
              {togglingId ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                  {t("app.common.loading")}
                </>
              ) : confirmToggleRow?.isActive ? (
                t("app.payroll.common.deactivate")
              ) : (
                t("app.payroll.common.activate")
              )}
            </button>
          </>
        }
      >
        <p className="mb-0 text-muted">
          {confirmToggleRow?.isActive
            ? t("app.payroll.incomeTax.softDeleteConfirm")
            : t("app.payroll.incomeTax.activateConfirm")}
        </p>
      </PayrollModal>

      <PayrollModal
        open={modalOpen}
        onClose={closeModal}
        title={modalMode === "create" ? t("app.payroll.incomeTax.newPolicy") : t("app.payroll.common.edit")}
        subtitle={t("app.payroll.incomeTax.modalTitle")}
        titleId="payroll-income-tax-modal-title"
        size="large"
        variant="salary"
        headerIcon={<i className="bi bi-percent" />}
        footer={
          <>
            <button type="button" className="att-btn att-btn--ghost" onClick={closeModal} disabled={saving}>
              {t("app.payroll.common.cancel")}
            </button>
            <button type="button" className="att-btn att-btn--save-primary" onClick={handleSavePolicy} disabled={saving || loadingDetail}>
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                  {t("app.payroll.common.saving")}
                </>
              ) : (
                t("app.payroll.common.save")
              )}
            </button>
          </>
        }
      >
        <div className="payroll-sc-modal-form payroll-sc-modal-form--salary">
          <div className="row g-2">
            <div className="col-12">
              <label className="att-modal-field-label" htmlFor="payroll-it-name">
                {t("app.payroll.incomeTax.fieldTaxName")}
              </label>
              <input
                id="payroll-it-name"
                className="form-control"
                value={policyName}
                onChange={(e) => setPolicyName(e.target.value)}
                placeholder={t("app.payroll.incomeTax.fieldTaxNamePh")}
                autoComplete="off"
              />
            </div>
            <div className="col-12">
              <label className="att-modal-field-label" htmlFor="payroll-it-description">
                {t("app.payroll.incomeTax.fieldDescription")}
              </label>
              <textarea
                id="payroll-it-description"
                className="form-control payroll-sc-modal-textarea"
                rows={2}
                value={policyDescription}
                onChange={(e) => setPolicyDescription(e.target.value)}
                placeholder={t("app.payroll.incomeTax.fieldDescriptionPh")}
              />
            </div>
            <div className="col-12">
              <label className="att-modal-field-label" htmlFor="payroll-it-office">
                {t("app.payroll.incomeTax.fieldWorkplace")}
              </label>
              <select
                id="payroll-it-office"
                className="form-select"
                value={modalOfficeId}
                onChange={(e) => setModalOfficeId(e.target.value)}
                disabled={workplaces.length === 0 && !modalOfficeId}
                aria-describedby="payroll-it-office-hint"
              >
                <option value="">{t("app.payroll.incomeTax.fieldWorkplaceCompanyWide")}</option>
                {modalOfficeOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {officeChipLabel(o)}
                  </option>
                ))}
              </select>
              <small className="text-muted d-block mt-1" id="payroll-it-office-hint">
                {t("app.payroll.incomeTax.fieldWorkplaceHint")}
              </small>
            </div>
            <div className="col-12">
              <label className="att-modal-field-label" htmlFor="payroll-it-type">
                {t("app.payroll.incomeTax.fieldTaxType")}
              </label>
              <select
                id="payroll-it-type"
                className="form-select"
                value={policyTaxType}
                onChange={(e) => {
                  const v = e.target.value === "fixed" ? "fixed" : "slab";
                  setPolicyTaxType(v);
                  if (v === "slab" && slabs.length === 0) setSlabs([emptyTaxSlab()]);
                }}
              >
                <option value="slab">{t("app.payroll.incomeTax.typeSlab")}</option>
                <option value="fixed">{t("app.payroll.incomeTax.typeFixed")}</option>
              </select>
            </div>
          </div>

          {policyTaxType === "slab" ? (
            <>
              <aside className="payroll-it-progressive-info mt-3" aria-label={t("app.payroll.incomeTax.progressiveInfoTitle")}>
                <div className="payroll-it-progressive-info__head">
                  <i className="bi bi-layers-half payroll-it-progressive-info__icon" aria-hidden />
                  <span className="payroll-it-progressive-info__title">{t("app.payroll.incomeTax.progressiveInfoTitle")}</span>
                </div>
                <p className="payroll-it-progressive-info__lead">{t("app.payroll.incomeTax.progressiveInfoLead")}</p>
                <p className="payroll-it-progressive-info__example">{t("app.payroll.incomeTax.progressiveInfoExample")}</p>
                <dl className="payroll-it-glossary">
                  <div className="payroll-it-glossary__item">
                    <dt>{t("app.payroll.incomeTax.glossaryTaxBand")}</dt>
                    <dd>{t("app.payroll.incomeTax.glossaryTaxBandDef")}</dd>
                  </div>
                  <div className="payroll-it-glossary__item">
                    <dt>{t("app.payroll.incomeTax.glossaryMarginalRate")}</dt>
                    <dd>{t("app.payroll.incomeTax.glossaryMarginalRateDef")}</dd>
                  </div>
                  <div className="payroll-it-glossary__item">
                    <dt>{t("app.payroll.incomeTax.glossaryEffectiveRate")}</dt>
                    <dd>{t("app.payroll.incomeTax.glossaryEffectiveRateDef")}</dd>
                  </div>
                </dl>
              </aside>

              <hr className="payroll-sc-modal-form__divider" />

              <p className="payroll-it-slabs-lead text-muted small mb-2">{t("app.payroll.incomeTax.slabsSectionLead")}</p>
              <div className="payroll-ps-components-head">
                <h4 className="mb-0">{t("app.payroll.incomeTax.slabsTitle")}</h4>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addSlab}>
                  <i className="bi bi-plus-lg me-1" aria-hidden />
                  {t("app.payroll.incomeTax.addSlab")}
                </button>
              </div>

              <div className="payroll-slab-rows mt-2">
                {slabs.map((s, idx) => (
                  <div key={s.id} className="payroll-slab-row payroll-slab-row--tax-band">
                    <div className="payroll-slab-band-meta">
                      <span className="payroll-modal-field-label-sm">{t("app.payroll.incomeTax.slabBandN", { n: idx + 1 })}</span>
                    </div>
                    <div>
                      <label className="payroll-modal-field-label-sm" htmlFor={`payroll-it-lower-${s.id}`}>
                        {t("app.payroll.incomeTax.slabLowerBound")}
                      </label>
                      <input
                        id={`payroll-it-lower-${s.id}`}
                        className="form-control form-control-sm mt-1"
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        value={s.lowerBound}
                        onChange={(e) => updateSlab(s.id, { lowerBound: e.target.value })}
                        aria-describedby={`payroll-it-lower-hint-${s.id}`}
                      />
                      <small className="text-muted d-block mt-1" id={`payroll-it-lower-hint-${s.id}`}>
                        {t("app.payroll.incomeTax.slabLowerBoundHint")}
                      </small>
                    </div>
                    <div className="payroll-slab-upper-cell">
                      <label className="payroll-modal-field-label-sm" htmlFor={`payroll-it-upper-${s.id}`}>
                        {t("app.payroll.incomeTax.slabUpperBound")}
                      </label>
                      <input
                        id={`payroll-it-upper-${s.id}`}
                        className="form-control form-control-sm mt-1"
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        disabled={s.unboundedUpper}
                        value={s.unboundedUpper ? "" : s.upperBound}
                        placeholder={s.unboundedUpper ? t("app.payroll.incomeTax.slabInfinityPlaceholder") : ""}
                        onChange={(e) => updateSlab(s.id, { upperBound: e.target.value })}
                        aria-describedby={`payroll-it-upper-hint-${s.id}`}
                      />
                      <div className="form-check payroll-it-unbounded-check mt-2 mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`payroll-it-unbounded-${s.id}`}
                          checked={s.unboundedUpper}
                          onChange={(e) =>
                            updateSlab(s.id, {
                              unboundedUpper: e.target.checked,
                              upperBound: e.target.checked ? "" : s.upperBound,
                            })
                          }
                        />
                        <label className="form-check-label small" htmlFor={`payroll-it-unbounded-${s.id}`}>
                          {t("app.payroll.incomeTax.slabUpperUnbounded")}
                        </label>
                      </div>
                      <small className="text-muted d-block mt-1" id={`payroll-it-upper-hint-${s.id}`}>
                        {s.unboundedUpper ? t("app.payroll.incomeTax.slabUpperUnboundedHint") : t("app.payroll.incomeTax.slabUpperBoundHint")}
                      </small>
                    </div>
                    <div>
                      <label className="payroll-modal-field-label-sm" htmlFor={`payroll-it-rate-${s.id}`}>
                        {t("app.payroll.incomeTax.slabTaxRatePercent")}
                      </label>
                      <input
                        id={`payroll-it-rate-${s.id}`}
                        className="form-control form-control-sm mt-1"
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        value={s.taxRatePercent}
                        onChange={(e) => updateSlab(s.id, { taxRatePercent: e.target.value })}
                        aria-describedby={`payroll-it-rate-hint-${s.id}`}
                      />
                      <small className="text-muted d-block mt-1" id={`payroll-it-rate-hint-${s.id}`}>
                        {t("app.payroll.incomeTax.slabTaxRateHint")}
                      </small>
                    </div>
                    <div className="d-flex align-items-end justify-content-end">
                      <button
                        type="button"
                        className="btn btn-light btn-sm"
                        onClick={() => removeSlab(s.id)}
                        disabled={slabs.length <= 1}
                        aria-label={t("app.payroll.common.remove")}
                      >
                        <i className="bi bi-x-lg" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="mt-3">
              <hr className="payroll-sc-modal-form__divider" />
              <p className="text-muted small mb-3">{t("app.payroll.incomeTax.fixedPolicyNoSlabsHint")}</p>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="att-modal-field-label" htmlFor="payroll-it-fixed-amount-type">
                    {t("app.payroll.incomeTax.fieldAmountType")}
                  </label>
                  <select
                    id="payroll-it-fixed-amount-type"
                    className="form-select"
                    value={fixedAmountType}
                    onChange={(e) => setFixedAmountType(/** @type {FixedTaxAmountKind} */ (e.target.value))}
                    aria-describedby="payroll-it-fixed-amount-type-hint"
                  >
                    {FIXED_AMOUNT_KIND_OPTIONS.map(([val, i18nKey]) => (
                      <option key={val} value={val}>
                        {t(`app.payroll.incomeTax.${i18nKey}`)}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted d-block mt-1" id="payroll-it-fixed-amount-type-hint">
                    {t("app.payroll.incomeTax.fieldAmountTypeHint")}
                  </small>
                </div>
                <div className="col-12 col-md-6">
                  <label className="att-modal-field-label" htmlFor="payroll-it-fixed-amount-value">
                    {fixedAmountType === "percentage"
                      ? t("app.payroll.incomeTax.fieldPercentage")
                      : t("app.payroll.incomeTax.fieldFixedAmount")}
                  </label>
                  <div
                    className={
                      fixedAmountType === "percentage" ? "input-group payroll-it-pct-input-group flex-nowrap" : undefined
                    }
                  >
                    <input
                      id="payroll-it-fixed-amount-value"
                      className="form-control"
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      value={fixedAmountValue}
                      onChange={(e) => setFixedAmountValue(e.target.value)}
                      placeholder={fixedAmountType === "percentage" ? "0" : "0.00"}
                      aria-describedby="payroll-it-fixed-amount-value-hint"
                    />
                    {fixedAmountType === "percentage" ? (
                      <span className="input-group-text" id="payroll-it-fixed-pct-suffix">
                        %
                      </span>
                    ) : null}
                  </div>
                  <small className="text-muted d-block mt-1" id="payroll-it-fixed-amount-value-hint">
                    {fixedAmountType === "percentage"
                      ? t("app.payroll.incomeTax.fieldPercentageHint")
                      : t("app.payroll.incomeTax.fieldFixedAmountHint")}
                  </small>
                </div>
              </div>
            </div>
          )}
        </div>
      </PayrollModal>
    </div>
  );
};

export default IncomeTaxesPage;
